import React, { useEffect, useState, useRef, useCallback } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import DistrictSchemeTable from './Tables/DistrictSchemeTable';
import axios from 'axios';
import { NavLink } from 'react-router-dom';
import SchemeCountsTable from './Tables/SchemeCountsTable';
import MapUpdater from './MapUpdater';
import MaskLayer from './MaskLayer';
import FilterSidebar from './FilterSidebar/FilterSidebar';
import SchemeDropdown from './Tables/SchemeDropdown';
import { getSchemeList, getStateWiseData } from './apiService';
import satellite from '../../assets/images/satellite_map_img.png';
import terrain from '../../assets/images/satellite_map2.png';
import osm from '../../assets/images/osm_map_img.png';
import StateWiseCountsTable from './Tables/StateWiseCountsTable';
import FinancialYearFilter from './Tables/FinancialYearFilter';
import DistrictSchemeVisualization from './Tables/DistrictSchemeVisualization';
import StateSchemeVisualization from './Tables/StateSchemeVisualization';
import PolygonLayer from './Tables/PolygonLayer';
import { SCHEME_MAPPING } from './FilterSidebar/utils/schemeMapping';
import filtericon from '../../assets/images/filter_icon.svg';
import ATMASidebarDetails from './Tables/ATMASidebarDetails';
import BeneficiaryDetailsModal from './Tables/BeneficiaryDetailsModal';
const API_BASE_URL = process.env.NODE_ENV === "development" 
  ? process.env.REACT_APP_DEV_MODE 
  : process.env.REACT_APP_PRO_MODE;
const WMSTileLayer = ({ url, layers, ...props }) => {
  const map = useMap();
  
  useEffect(() => {
    const wmsLayer = L.tileLayer.wms(url, {
      layers: layers,
      format: 'image/png',
      transparent: true,
      attribution: 'Bhuvan NRSC',
      maxZoom: 18,
      version: '1.1.1',
      srs: 'EPSG:3857',
      Maxresolution: 156543.033928,
      styles: '',
      ...props
    }).addTo(map);

    return () => {
      map.removeLayer(wmsLayer);
    };
  }, [map, url, layers, props]);

  return null;
};

// Enhanced API service with better error handling
const apiService = {
  async makeApiCall(endpoint, data) {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          "Content-Type": "application/json",
          ...(token && { "Authorization": `Bearer ${token}` }),
          "Connection": "keep-alive", // Keep connection alive
          "Accept-Encoding": "gzip, deflate", // Accept compressed responses
        },
        timeout: 120000, // Increase to 2 minutes
        timeoutErrorMessage: 'Request timed out. The server is taking too long to respond.'
      };

      console.log(`API Call: ${endpoint}`, {
        environment: process.env.NODE_ENV,
        baseURL: API_BASE_URL,
        dataSize: JSON.stringify(data).length
      });

      const response = await axios.post(
        `${API_BASE_URL}${endpoint}`,
        data,
        config
      );

      return response.data;
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        console.error('Timeout occurred. The server might be overloaded.');
        throw new Error('The request is taking too long. Please try again later.');
      }
      throw error;
    }
  }
};

// Updated TopSchemesCard component
const TopSchemesCard = ({ topSchemes, isLoading, selectedFinancialYear }) => {
  if (isLoading) {
    return (
      <div className="mapRightBhuvanAnalytics mt-1">
        <div className="mapheader">
          <h2 className="mb-0">Top 5 Schemes</h2>
        </div>
        <div className="card-body">
          <div className="text-center">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 mb-0">Loading top schemes...</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle empty data
  if (!topSchemes || topSchemes.length === 0) {
    return (
      <div className="mapRightBhuvanAnalytics mt-1">
        <div className="mapheader">
          <h2 className="mb-0">Top 5 Schemes {selectedFinancialYear && `(${selectedFinancialYear})`}</h2>
        </div>
        <div className="card-body text-center">
          <div className="text-muted mb-2">
            <i className="bi bi-inbox" style={{fontSize: '2rem'}}></i>
          </div>
          <p className="text-muted mb-0">
            {selectedFinancialYear === '2026-2027' 
              ? "Data for 2026-2027 will be available soon"
              : "No scheme data available"
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mapRightBhuvanAnalytics mt-1">
      <div className="mapheader">
        <h2 className="mb-0">Top 5 Schemes {selectedFinancialYear && `(${selectedFinancialYear})`}</h2>
      </div>
      
      <div className="table-responsive">
        <table className="table table-bordered table-striped table-hover">
          <thead>
            <tr>
              <th>S.No.</th>
              <th>Scheme Name</th>
              <th>Surveys</th>
            </tr>
          </thead>
          <tbody>
            {topSchemes.map((scheme, index) => (
              <tr key={scheme.schemeCode || index}>
                <td className="text-center">{index + 1}</td>
                <td>{scheme.schemeName}</td>
                <td className="fw-bold">{parseInt(scheme.totalSurvey).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const GIS = () => {
  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showSchemesDropdown, setShowSchemesDropdown] = useState(false);
  const [tempSelectedSchemes, setTempSelectedSchemes] = useState([]);
  const [schemeSearchText, setSchemeSearchText] = useState('');
  const [userVisible, setUserVisible] = useState(false);
  const [mapService, setMapService] = useState("bhuvan");
  const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]);
  const [mapZoom, setMapZoom] = useState(4);
  const [stateData, setStateData] = useState(null);
  const [districtData, setDistrictData] = useState(null);
  const [villageBoundaries, setVillageBoundaries] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [districtWiseData, setDistrictWiseData] = useState([]);
  const [districtWiseSchemeData, setDistrictWiseSchemeData] = useState({});
  const [loadingDistrictData, setLoadingDistrictData] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [showDistrictTable, setShowDistrictTable] = useState(true);
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [filteredDistricts, setFilteredDistricts] = useState([]);
  const [filteredVillages, setFilteredVillages] = useState([]);
  const [filteredTopSchemes, setFilteredTopSchemes] = useState([]);
  const [displayAnalytics, setDisplayAnalytics] = useState(true);
  const [selectedSchemes, setSelectedSchemes] = useState([]);
  const [allIndiaSchemeCounts, setAllIndiaSchemeCounts] = useState({});
  const [SelectedDropDownName, setSelectedDropDownName] = useState("India");
  const [schemeCounts, setSchemeCounts] = useState([]);
  const [schemeColors, setSchemeColors] = useState({});
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [isTopSchemesLoading, setIsTopSchemesLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStateCode, setSelectedStateCode] = useState(null);
  const [tileLayerLoaded, setTileLayerLoaded] = useState(false);
  const [boundariesLoaded, setBoundariesLoaded] = useState(false);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [currentView, setCurrentView] = useState('india');
  const [stateWiseSchemeData, setStateWiseSchemeData] = useState({});
  const [imageCache, setImageCache] = useState({});
  const [showTopSchemes, setShowTopSchemes] = useState(true);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [stateWiseData, setStateWiseData] = useState([]);
  const [loadingStateData, setLoadingStateData] = useState(false);
  const [topSchemes, setTopSchemes] = useState([]);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState('2026-2027');
  const [isFinancialYearLoading, setIsFinancialYearLoading] = useState(false);
  const storedValue = JSON.parse(sessionStorage.getItem('selectedValue'));
  const dropdownRef = useRef(null);
  const mapRef = useRef(null);
  const [resetFinancialYear, setResetFinancialYear] = useState(false);
  const schemeLayerRefs = useRef([]);
  const [selectedDistrictForPolygons, setSelectedDistrictForPolygons] = useState(null);
  const [filteredSchemeCounts, setFilteredSchemeCounts] = useState([]);
  const [showATMAPopup, setShowATMAPopup] = useState(false);
const [selectedATMAPopupData, setSelectedATMAPopupData] = useState(null);
const [selectedBeneficiaryData, setSelectedBeneficiaryData] = useState(null);
const [showBeneficiaryModal, setShowBeneficiaryModal] = useState(false);
  // FIXED: Track loading states separately
  const [loadingStates, setLoadingStates] = useState({
    allSchemes: false,
    topSchemes: false,
    stateWise: false
  });
  
  // Sidebar minimization state
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);

  // Map service URLs
  const bhuvanLayerUrl = 'https://bhuvan-ras1.nrsc.gov.in/tilecache/tilecache.py';
  const terrainLayerUrl = "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png";
  const osmLayerUrl = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
  const satelliteLayerUrl = "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}";

  // FIXED Financial Year Conversion
  const convertFinancialYearForAPI = (financialYear) => {
    console.log(" Converting Financial Year:", {
      input: financialYear,
      type: typeof financialYear
    });
    
    if (!financialYear || financialYear === "ALL") return "";
    
    // Handle the full financial year format "2026-2027"
    if (financialYear.includes('-')) {
      const startYear = financialYear.split('-')[0];
      console.log(" Converted:", financialYear, "→", startYear);
      return startYear;
    }
    
    // If it's already just a year, return as-is
    if (financialYear.length === 4 && !isNaN(financialYear)) {
      return financialYear;
    }
    
    console.log(" Unknown financial year format:", financialYear);
    return "";
  };
// FIXED: Data fetching with proper helper functions
const fetchAllDataForFinancialYear = async (financialYear, options = {}) => {
  const { preserveStateWiseData = false } = options;
  
  console.log(` fetchAllDataForFinancialYear called:`, { 
    financialYear, 
    preserveStateWiseData,
    selectedScheme: selectedScheme?.schemeName 
  });

  setIsFinancialYearLoading(true);

  // Set loading states
  setLoadingStates(prev => ({
    ...prev,
    allSchemes: true,
    topSchemes: true
  }));

  try {
    const apiFinancialYear = convertFinancialYearForAPI(financialYear);
    
    console.log(`📊 Fetching data for FY: ${financialYear} -> API: ${apiFinancialYear}`);

    // ONLY clear data that needs to be refreshed
    setSchemeCounts([]);
    setFilteredSchemeCounts([]);
    setTopSchemes([]);
    setFilteredTopSchemes([]);

    // Only clear state-wise data if not preserving it
    if (!preserveStateWiseData) {
      setStateWiseData([]);
      setStateWiseSchemeData({});
    }

    // SEPARATE API CALL for (flag 0)
    console.log(" Fetching with flag: 0");
    const [allSchemesResponse, topSchemesResponse] = await Promise.all([
      // All schemes API call (flag 0)
      apiService.makeApiCall('/api/GT/GetSchemeCount', {
        flag: 0,
        SchemeName: "",
        FinancialYear: apiFinancialYear
      }),
      
      // Top 5 schemes API call (flag 3)
      apiService.makeApiCall('/api/GT/GetSchemeCount', {
        flag: 3,
        SchemeName: "",
        FinancialYear: apiFinancialYear
      })
    ]);

    console.log("✅ API Responses received in parallel");

    console.log(" Separate API Responses:", {
      allSchemesFlag: 0,
      allSchemesData: allSchemesResponse?.dataContent,
      topSchemesFlag: 3, 
      topSchemesData: topSchemesResponse?.dataContent
    });

    // Process ALL SCHEMES DATA from flag 0 API
    const allSchemes = (allSchemesResponse?.dataContent || [])
      .map(scheme => {
        return {
          schemeCode: scheme.schemeCode || scheme.SchemeCode || '',
          schemeName: scheme.schemeName || scheme.SchemeName || '',
          totalSurvey: parseInt(scheme.totalSurvey || scheme.TotalSurvey || 0),
          financialYear: financialYear === "ALL" ? "All Years" : financialYear
        };
      })
      .filter(scheme => scheme.schemeName && scheme.schemeName.trim() !== '')
      .sort((a, b) => b.totalSurvey - a.totalSurvey);

    // Process TOP 5 SCHEMES DATA from flag 3 API (use as-is)
    const top5Schemes = (topSchemesResponse?.dataContent || [])
      .map(scheme => {
        return {
          schemeCode: scheme.schemeCode || scheme.SchemeCode || '',
          schemeName: scheme.schemeName || scheme.SchemeName || '',
          totalSurvey: parseInt(scheme.totalSurvey || scheme.TotalSurvey || 0),
          financialYear: financialYear
        };
      })
      .filter(scheme => scheme.schemeName && scheme.schemeName.trim() !== '');

    console.log(`Separate data loaded for ${financialYear}:`, {
      allSchemesFromFlag0: allSchemes.length,
      top5SchemesFromFlag3: top5Schemes.length
    });

    // Update state with NEW data
    setSchemeCounts(allSchemes);
    setFilteredSchemeCounts(allSchemes);
    setTopSchemes(top5Schemes);
    setFilteredTopSchemes(top5Schemes);

    // Generate colors for schemes
    if (allSchemes.length > 0) {
      const colors = {};
      allSchemes.forEach(scheme => {
        colors[scheme.schemeName] = `#${Math.floor(Math.random()*16777215).toString(16).padStart(6, '0')}`;
      });
      setSchemeColors(colors);
    }

    // If we have a selected scheme AND we're preserving state data, refresh it
    if (selectedScheme && preserveStateWiseData) {
      console.log(" Refreshing state-wise data for selected scheme with new financial year");
      await fetchStateWiseData(selectedScheme.schemeName, financialYear);
    }

  } catch (error) {
    console.error("Error fetching separate data for financial year:", financialYear, error);
    setError(`Failed to load data for ${financialYear}: ${error.message}`);
  } finally {
    setIsFinancialYearLoading(false);
    setLoadingStates(prev => ({
      ...prev,
      allSchemes: false,
      topSchemes: false
    }));
  }
};

  // FIXED: Simplified state-wise data fetching
  const fetchStateWiseData = async (schemeName, financialYear = selectedFinancialYear) => {
    console.log(" fetchStateWiseData called:", { schemeName, financialYear });
    
    setLoadingStateData(true);
    setLoadingStates(prev => ({ ...prev, stateWise: true }));
    
    try {
      const apiFinancialYear = convertFinancialYearForAPI(financialYear);

      console.log(" API Payload for state-wise data:", {
        flag: 2,
        schemeName: schemeName,
        financialYear: apiFinancialYear
      });

      const response = await apiService.makeApiCall('/api/GT/GetSchemeCount', {
        flag: 2,
        schemeName: schemeName,
        financialYear: apiFinancialYear
      });

      console.log(" State-wise API Response:", {
        dataContentLength: response?.dataContent?.length || 0
      });

      let rawData = response?.dataContent || [];
      
      const processedData = rawData.map((item) => {
        const stateName = item.stateName || item.State || item.STATE_NAME || 'Unknown';
        let totalSurvey = item.totalSurvey || item.TotalSurvey || item.count || 0;
        
        if (typeof totalSurvey === 'string') {
          totalSurvey = totalSurvey.replace(/,/g, '');
        }

        return {
          stateName: stateName,
          totalSurvey: parseInt(totalSurvey) || 0,
          financialYear: financialYear
        };
      }).filter(item => item.stateName !== 'Unknown');

      console.log(" Processed State Data:", {
        count: processedData.length,
        totalSurveys: processedData.reduce((sum, item) => sum + item.totalSurvey, 0)
      });

      setStateWiseData(processedData);
      
      const stateCountsMap = {};
      processedData.forEach(item => {
        stateCountsMap[item.stateName] = item.totalSurvey;
      });
      
      setStateWiseSchemeData(stateCountsMap);
      console.log(" State-wise data updated successfully");

    } catch (error) {
      console.error(" Error in fetchStateWiseData:", error.message);
      // Clear state data on error
      setStateWiseData([]);
      setStateWiseSchemeData({});
    } finally {
      setLoadingStateData(false);
      setLoadingStates(prev => ({ ...prev, stateWise: false }));
    }
  };

 
const fetchDistrictWiseData = async (stateLGD, schemeName = "", financialYear = selectedFinancialYear) => {
  console.log(" fetchDistrictWiseData called:", { stateLGD, schemeName, financialYear });
  
  setLoadingDistrictData(true);
  
  try {
    const apiFinancialYear = convertFinancialYearForAPI(financialYear);

    const requestBody = {
      flag: 1,
      stateCode: stateLGD.toString(),
      SchemeName: schemeName,
      FinancialYear: apiFinancialYear
    };

    console.log(" District-wise API Payload:", requestBody);

    const response = await apiService.makeApiCall('/api/GT/GetSchemeCount', requestBody);

    console.log(" RAW District-wise API Response:", response);

    let rawData = response?.dataContent || [];
    
    // FIXED: Use the correct field names from your API response
    const processedData = rawData.map((item, index) => {
      console.log(`Processing district item ${index}:`, item);
      
      // Use the exact field names from your API response
      const districtName = item.districtName || 'Unknown';
      let totalSurvey = item.totalSurvey || "0";
      
      // Handle string numbers with commas and other formatting
      if (typeof totalSurvey === 'string') {
        totalSurvey = totalSurvey.replace(/,/g, '').replace(/"/g, '');
      }

      // Parse to integer, default to 0 if NaN
      const parsedSurvey = parseInt(totalSurvey) || 0;

      console.log(` Processed district: ${districtName} -> ${parsedSurvey} surveys`);

      return {
        districtName: districtName,
        totalSurvey: parsedSurvey,
        districtCode: item.districtCode ? item.districtCode.toString() : null,
        financialYear: item.financialYear || financialYear,
        schemeName: item.schemeName,
        stateName: item.stateName
      };
    }).filter(item => item.districtName !== 'Unknown' && item.districtName);

    console.log(" FINAL Processed District Data:", {
      count: processedData.length,
      districts: processedData.map(d => ({ name: d.districtName, surveys: d.totalSurvey })),
      totalSurveys: processedData.reduce((sum, item) => sum + item.totalSurvey, 0)
    });

    setDistrictWiseData(processedData);
    
    // Create a mapping of district names to counts for visualization
    const districtCountsMap = {};
    processedData.forEach(item => {
      districtCountsMap[item.districtName] = item.totalSurvey;
    });
    
    setDistrictWiseSchemeData(districtCountsMap);
    console.log(" District-wise data updated successfully:", districtCountsMap);

  } catch (error) {
    console.error(" Error in fetchDistrictWiseData:", error.message);
    console.error("Error details:", error);
    setDistrictWiseData([]);
    setDistrictWiseSchemeData({});
  } finally {
    setLoadingDistrictData(false);
  }
};

  // FIXED: Completely rewrote financial year handler
  const handleFinancialYearChange = async (year) => {
    console.log(` Financial Year Changed to: ${year}`);
    
    // Update financial year state immediately
    setSelectedFinancialYear(year);
    
    // Determine if we should preserve state-wise data
    const shouldPreserveStateData = selectedScheme !== null;
    
    console.log(" Financial Year Change Details:", {
      newYear: year,
      selectedScheme: selectedScheme?.schemeName,
      preserveStateData: shouldPreserveStateData
    });
    
    // Fetch new data with preservation option
    await fetchAllDataForFinancialYear(year, { 
      preserveStateWiseData: shouldPreserveStateData 
    });
  };

  // FIXED: Simplified scheme change handler
  const handleSchemeChange = async (scheme) => {
    console.log(" Scheme changed to:", scheme?.schemeName);
    setUserVisible(false);
    setSelectedScheme(scheme);
    setShowTopSchemes(!scheme);

    if (!scheme) {
      // Clear data when no scheme is selected
      setStateWiseData([]);
      setStateWiseSchemeData({});
      setDistrictWiseData([]);
      setDistrictWiseSchemeData({});
      return;
    }

    // Fetch state-wise data for the selected scheme
    await fetchStateWiseData(scheme.schemeName, selectedFinancialYear);
    
    // If a state is selected, also refresh district data
    if (selectedState) {
      const stateLGD = selectedState.properties.STATE_LGD;
      if (stateLGD) {
        await fetchDistrictWiseData(stateLGD, scheme.schemeName);
      }
    }
  };

  // FIXED: Initial data load - only load basic data
  useEffect(() => {
    const defaultFinancialYear = '2026-2027';
    console.log(" GIS Component Mounted - Initial Data Fetch");
    fetchAllDataForFinancialYear(defaultFinancialYear);
  }, []);

  // FIXED: Add effect to refresh district data when financial year changes
  useEffect(() => {
    if (selectedState && selectedScheme) {
      const stateLGD = selectedState.properties.STATE_LGD;
      if (stateLGD) {
        fetchDistrictWiseData(stateLGD, selectedScheme.schemeName, selectedFinancialYear);
      }
    }
  }, [selectedFinancialYear]);

  // Toggle function for sidebar minimize
  const toggleSidebarMinimize = () => {
    setIsSidebarMinimized(!isSidebarMinimized);
  };

  const handleMapServiceChange = (service) => {
    setMapService(service);
  };

  // Helper functions
  const getFeatureBounds = (geometry) => {
    return geometry ? L.geoJSON(geometry).getBounds() : null;
  };

  // Map Controls Component
  const MapControls = ({ mapService, setMapService }) => {
    return (
      <div className="map-controls">
        {/* Map control buttons can be added here */}
      </div>
    );
  };

  const handleStateDataLoaded = useCallback((schemeName, data) => {
    setStateWiseData(data);

    // Create a mapping of state names to counts for the selected scheme
    const stateCountsMap = {};
    data.forEach(item => {
      stateCountsMap[item.stateName] = item.count;
    });
    setStateWiseSchemeData(stateCountsMap);
  }, []);

 const handleDistrictClick = useCallback((districtFeature) => {
  console.log(" District clicked:", districtFeature.properties.DISTRICT);
  
  // CLOSE THE FILTER SIDEBAR AUTOMATICALLY
  setUserVisible(false);
  
  // Add current state view to history with more complete information
  const historyEntry = {
    level: currentView, // Should be 'state' when coming from state view
    state: selectedState,
    district: null,
    hadScheme: !!selectedScheme,
    financialYear: selectedFinancialYear,
    stateWiseData: stateWiseData,
    stateWiseSchemeData: stateWiseSchemeData
  };
  
  setNavigationHistory(prev => [...prev, historyEntry]);
  
  setSelectedDistrict(districtFeature);
  setSelectedDistrictForPolygons(districtFeature);
  setShowDistrictTable(false);
  setCurrentView('district');
  setIsSidebarMinimized(true);
  
  // Zoom to the selected district
  const bounds = L.geoJSON(districtFeature).getBounds();
  if (bounds.isValid()) {
    setMapCenter(bounds.getCenter());
    setMapZoom(10);
  }
  
  setSelectedDropDownName(districtFeature.properties.DISTRICT);
  console.log(" History updated for district click:", [...navigationHistory, historyEntry].length, "entries");
}, [selectedState, currentView, selectedFinancialYear, navigationHistory, stateWiseData, stateWiseSchemeData]);

const handleBackToStateView = useCallback(() => {
    setSelectedDistrict(null);
    setSelectedDistrictForPolygons(null);
    setShowDistrictTable(true); // Show the district table again
  }, []);

const handleBackNavigation = () => {
  console.log(" Back navigation triggered", {
    historyLength: navigationHistory.length,
    currentView,
    selectedState: selectedState?.properties?.STATE,
    selectedDistrict: selectedDistrict?.properties?.DISTRICT
  });

  if (navigationHistory.length > 0) {
    const previousView = navigationHistory[navigationHistory.length - 1];
    const newHistory = [...navigationHistory];
    newHistory.pop();
    
    setNavigationHistory(newHistory);

    console.log(" Restoring previous view:", previousView);
    
    switch (previousView.level) {
      case 'district':
        // Going back from polygon view to district view
        setSelectedState(previousView.state); // Keep the state
        setSelectedDistrict(previousView.district);
        setSelectedDistrictForPolygons(previousView.district);
        setSelectedVillage(null);
        setCurrentView('district');
        setShowDistrictTable(false);
        setSelectedDropDownName(previousView.district?.properties?.DISTRICT || "District");
        setIsSidebarMinimized(true);
        if (previousView.hadScheme && previousView.stateWiseData) {
          setStateWiseData(previousView.stateWiseData);
          setStateWiseSchemeData(previousView.stateWiseSchemeData || {});
        }
        // Zoom to district bounds
        if (previousView.district?.geometry) {
          const bounds = getFeatureBounds(previousView.district.geometry);
          if (bounds) {
            setMapCenter(bounds.getCenter());
            setMapZoom(10);
          }
        }
        
        // Update filtered districts for the state
        if (previousView.state && districtData) {
          const stateDistricts = districtData.features.filter(
            d => d.properties.STATE_UT === previousView.state.properties.STATE
          );
          setFilteredDistricts(stateDistricts);
        }
        
        // Refresh district data if needed
        if (previousView.state && selectedScheme) {
          const stateLGD = previousView.state.properties.STATE_LGD;
          if (stateLGD) {
            fetchDistrictWiseData(stateLGD, selectedScheme?.schemeName, selectedFinancialYear);
          }
        }
        break;

      case 'state':
        // Going back from district to state
        setSelectedState(previousView.state);
        setSelectedDistrict(null);
        setSelectedDistrictForPolygons(null);
        setSelectedVillage(null);
        setCurrentView('state');
        setShowDistrictTable(true);
        setSelectedDropDownName(previousView.state?.properties?.STATE || "State");
        setIsSidebarMinimized(false);
        
        // Zoom to state bounds
        if (previousView.state?.geometry) {
          const bounds = getFeatureBounds(previousView.state.geometry);
          if (bounds) {
            setMapCenter(bounds.getCenter());
            setMapZoom(7);
          }
        }
        
        // Update filtered districts
        if (previousView.state && districtData) {
          const stateDistricts = districtData.features.filter(
            d => d.properties.STATE_UT === previousView.state.properties.STATE
          );
          setFilteredDistricts(stateDistricts);
        }
        break;

      case 'india':
        // Going back from state to India
        setSelectedState(null);
        setSelectedDistrict(null);
        setSelectedDistrictForPolygons(null);
        setSelectedVillage(null);
        setCurrentView('india');
        setShowDistrictTable(true);
        setSelectedDropDownName("India");
        setIsSidebarMinimized(false);
        setMapCenter([20.5937, 78.9629]);
        setMapZoom(4);
        setFilteredDistricts([]);
        
        break;

      default:
        resetSelection();
    }
  } else {
    resetSelection();
  }
};

const handleStateSelect = async (stateName) => {
  const stateFeature = stateData?.features.find(f => f.properties.STATE === stateName);
  if (stateFeature) {
    console.log(" State selected:", stateName);
    
    // CLOSE THE FILTER SIDEBAR AUTOMATICALLY
    setUserVisible(false);
    
    // Add current view to history
    const historyEntry = {
      level: currentView,
      state: selectedState, 
      district: selectedDistrict,
      hadScheme: !!selectedScheme,
      financialYear: selectedFinancialYear,
      stateWiseData: stateWiseData,
      stateWiseSchemeData: stateWiseSchemeData
    };
    
    setNavigationHistory(prev => [...prev, historyEntry]);
    
    // Update current state
    setSelectedState(stateFeature);
    setSelectedDistrict(null);
    setSelectedDistrictForPolygons(null);
    setSelectedVillage(null);
    setCurrentView('state');
    setShowDistrictTable(true);

    const stateDistricts = districtData?.features.filter(d => d.properties.STATE_UT === stateName) || [];
    setFilteredDistricts(stateDistricts);

    setSelectedDropDownName(stateFeature.properties.STATE);
    
    // Zoom to state
    const bounds = getFeatureBounds(stateFeature.geometry);
    if (bounds) {
      setMapCenter(bounds.getCenter());
      setMapZoom(7);
    }

    // Fetch district data
    const stateLGD = stateFeature.properties.STATE_LGD;
    if (stateLGD) {
      await fetchDistrictWiseData(stateLGD, selectedScheme?.schemeName);
    }

    console.log(" History updated for state selection:", [...navigationHistory, historyEntry].length, "entries");
  }
};
const handleDistrictSelect = useCallback((districtFeature) => {
  console.log(" District selected:", districtFeature.properties.DISTRICT);
  
  if (!selectedState) {
    console.error(" No state selected for district navigation");
    return;
  }
  
  // Add current state view to history
  const historyEntry = {
    level: 'state',
    state: selectedState,
    district: null
  };
  
  setNavigationHistory(prev => [...prev, historyEntry]);
  setSelectedDistrict(districtFeature);
  setSelectedDistrictForPolygons(districtFeature);
  setShowDistrictTable(false);
  setCurrentView('district');
  setSelectedDropDownName(districtFeature.properties.DISTRICT);
  setIsSidebarMinimized(true);
  // Zoom to district
  const bounds = L.geoJSON(districtFeature).getBounds();
  if (bounds.isValid()) {
    setMapCenter(bounds.getCenter());
    setMapZoom(10);
  }
  setSelectedDropDownName(districtFeature.properties.DISTRICT);
}, [selectedState]);

// FIXED: Enhanced reset function
// FIXED: Reset function that properly clears scheme when going to default view
const resetSelection = () => {
  console.log(" Resetting to default India view...");
  
  // Clear ALL selection data
  setSelectedState(null);
  setSelectedDistrict(null);
  setSelectedDistrictForPolygons(null);
  setSelectedVillage(null);
  setSelectedScheme(null); 
  setStateWiseData([]);
  setStateWiseSchemeData({});
  setDistrictWiseData([]);
  setDistrictWiseSchemeData({});
  setShowTopSchemes(true);
  setShowDistrictTable(true);
  setMapCenter([20.5937, 78.9629]);
  setMapZoom(4);
  setSelectedDropDownName("India");
  setNavigationHistory([]);
  setCurrentView('india');
  setFilteredDistricts([]);
  setFilteredVillages([]);
setMapService("bhuvan");
setIsSidebarMinimized(false);
setUserVisible(false);
setShowATMAPopup(false);
  setSelectedATMAPopupData(null);
  setShowBeneficiaryModal(false);
  setSelectedBeneficiaryData(null);
  console.log(" Reset complete - default India view");

  // Reset to default financial year and fetch fresh data
  const defaultFinancialYear = '2026-2027';
  setSelectedFinancialYear(defaultFinancialYear);
  fetchAllDataForFinancialYear(defaultFinancialYear);
  
  console.log(" Reset complete - all states cleared");
};


  const handleSchemeSelection = async (schemes) => {
    setSelectedSchemes(schemes);

    if (schemes.length === 1) {
      const scheme = schemes[0];
      await handleSchemeChange(scheme);
    } else {
      await handleSchemeChange(null);
    }
  };

  const clearAllSchemePolygons = () => {
    schemeLayerRefs.current.forEach((layer) => {
      if (mapRef.current && mapRef.current.removeLayer) {
        mapRef.current.removeLayer(layer);
      }
    });
    schemeLayerRefs.current = [];
  };

  const onEachFeature = (feature, layer) => {
    if (feature.properties?.STATE) {
      layer.bindTooltip(feature.properties.STATE, {
        permanent: false,
        direction: 'top',
        className: 'state-tooltip',
        offset: [0, -10]
      });

      layer.on({
        mouseover: (e) => {
          const layer = e.target;
          layer.setStyle({
            color: "#e3dfdcff",
            weight: 3,
            zIndex: 100
          });
          layer.openTooltip();
        },
        mouseout: (e) => {
          const layer = e.target;
          layer.setStyle({
            color: "transparent",
            weight: 2,
            zIndex: 100
          });
          layer.closeTooltip();
        },
        click: () => {
          handleStateSelect(feature.properties.STATE);
        }
      });
    }

    if (feature.properties?.DISTRICT) {
      layer.bindTooltip(feature.properties.DISTRICT, { permanent: false });
      layer.on({
        mouseover: () => layer.setStyle({
          color: "transparent",
          weight: 3,
          zIndex: 200,
          fillColor: "transparent",
          fillOpacity: 0
        }),
        mouseout: () => layer.setStyle({
          color: "transparent",
          weight: 1,
          zIndex: 200,
          fillColor: "transparent",
          fillOpacity: 0
        }),
        click: () => handleDistrictClick(feature)
      });
    }

    if (feature.properties?.Vill_name) {
      layer.bindTooltip(feature.properties.Vill_name, { permanent: false });
      layer.on({
        mouseover: () => layer.setStyle({ color: "transparent", weight: 2, zIndex: 300 }),
        mouseout: () => layer.setStyle({ color: "transparent", weight: 1, zIndex: 300 }),
      });
    }
  };

  // FilterSidebar props
  const filterSidebarProps = {
    userVisible,
    selectedState,
    selectedDistrict,
    selectedVillage,
    filteredDistricts,
    filteredVillages,
    stateData,
    onStateSelect: handleStateSelect,
    onDistrictSelect: handleDistrictClick, // Use the updated handler
    onReset: resetSelection,
    schemeSelectorProps: {
      storedValue,
      selectedSchemes,
      setSelectedSchemes,
    },
    mapService,
    setMapService,
    selectedFinancialYear,
    onFinancialYearChange: handleFinancialYearChange,
    financialYearLoading: isFinancialYearLoading,
    schemeCounts,
    selectedScheme,
    onSchemeChange: handleSchemeChange,
    schemeColors,
    onStateDataLoaded: handleStateDataLoaded,
    onBackNavigation: handleBackNavigation,
    currentView,
    hasHistory: navigationHistory.length > 0,
    navigationHistory,
    onMapServiceChange: handleMapServiceChange
  };
const hasHistory = navigationHistory.length > 0;

  // Loader component
  const Loader = () => (
    <div style={{
      position: "absolute",
      top: 0,
      left: 0,
      height: "100%",
      width: "100%",
      backgroundColor: "rgba(255, 255, 255, 0.7)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 999
    }}>
      <div className="spinner" />
    </div>
  );

  // Effect for map size adjustment
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.invalidateSize();
    }
  }, [mapService, isSidebarMinimized]);

  // Effect for boundaries loading
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [stateRes, districtRes] = await Promise.all([
          fetch("/stateboundary.json"),
          fetch("/Districtboundary.json")
        ]);

        if (!stateRes.ok || !districtRes.ok) throw new Error("Failed to load data");

        const stateJson = await stateRes.json();
        setStateData(stateJson);

        const districtJson = await districtRes.json();
        setDistrictData(districtJson);
        setBoundariesLoaded(true);

      } catch (error) {
        console.error("Error loading data:", error);
        setBoundariesLoaded(true);
      }
    };

    fetchData();
  }, []);

  // Effect for click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSchemesDropdown(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Effect for hiding top schemes when state data is shown
  useEffect(() => {
    if (selectedScheme && stateWiseData.length > 0) {
      setShowTopSchemes(false);
    }
  }, [selectedScheme, stateWiseData]);

  // Effect for tile layer loading
  useEffect(() => {
    if (tileLayerLoaded && boundariesLoaded) {
      setIsMapLoading(false);
    }
  }, [tileLayerLoaded, boundariesLoaded]);

  return (
    <div className="mapbg">
      {isMapLoading && (
        <div className="map-loading-overlay" style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000
        }}>
          <div className="text-center">
            <div className="spinner-border text-primary" style={{width: "3rem", height: "3rem"}} role="status">
              <span className="visually-hidden">Loading Map...</span>
            </div>
            <p className="mt-3">Loading map data...</p>
          </div>
        </div>
      )}
      
      <div className="container-fluid">
        <div className="row">
          {/* Map Column */}
          <div className={`${isSidebarMinimized ? 'col-12' : 'col-md-8'} px-0 position-relative`}>
            {/* Sidebar Toggle Button */}
            <button 
              className="minimize-toggle-btn"
              onClick={toggleSidebarMinimize}
              title={isSidebarMinimized ? "Show Sidebar" : "Minimize Sidebar"}
            >
              <i className={`bi ${isSidebarMinimized ? 'bi-chevron-left' : 'bi-chevron-right'}`}></i>
            </button>

            <div className="map_filterbg">
<button type="button" className="filter_btn" onClick={() => setUserVisible(prev => !prev)}>
<img src={filtericon} className="filterimg" alt="filter" />
</button>
</div>



 <div className="new_filterbuttonwrp">

<FilterSidebar {...filterSidebarProps} />
</div>

<div className="bottombtn_wrp">
  <div className="map-btn-group">

    <button
      className={`btn btn-success bottombtn_osm terrain-btn ${mapService === "terrain" ? "active" : ""}`}
      onClick={() => handleMapServiceChange("terrain")}
    >
      <img src={terrain} alt="Terrain" /> Terrain
    </button>

    <div className="hover-buttons">
      <button
        className={`btn btn-success bottombtn_osm ${mapService === "osm" ? "active" : ""}`}
        onClick={() => handleMapServiceChange("osm")}
      >
        <img src={osm} alt="OSM" /> OSM
      </button>

      <button
        className={`btn btn-success bottombtn_osm ${mapService === "satellite" ? "active" : ""}`}
        onClick={() => handleMapServiceChange("satellite")}
      >
        <img src={satellite} alt="Satellite" /> Satellite
      </button>
    </div>

  </div>
</div>

            <div style={{ height: "81vh", position: "relative"}}
            >
              {!boundariesLoaded && <Loader />}
              <MapContainer
                center={mapCenter}
                zoom={mapZoom}
                minZoom={4}
                maxZoom={18}
                style={{ height: "100%", width: "100%" }}
                dragging={true}
                touchZoom={false}
                scrollWheelZoom={true}
                doubleClickZoom={true}
                zoomControl={true}
                attributionControl={true}
                doubleClickDrag={true}
                ref={mapRef}
              >
                <MapUpdater center={mapCenter} zoom={mapZoom} />
                {mapService === "bhuvan" && (
                 <WMSTileLayer
    url={bhuvanLayerUrl}
    layers="bhuvan_wbase_ind"
  />
                )}
                {mapService === "bhuvan" && <TileLayer url={bhuvanLayerUrl} />}
                {mapService === "terrain" && <TileLayer url={terrainLayerUrl} />}
                {mapService === "osm" && <TileLayer url={osmLayerUrl} />}
                {mapService === "satellite" && <TileLayer url={satelliteLayerUrl} />}

                <MapControls mapService={mapService} setMapService={setMapService} />
                
                <StateSchemeVisualization
                  countryData={stateData}
                  selectedScheme={selectedScheme}
                  selectedFinancialYear={selectedFinancialYear}
                  selectedState={selectedState}
                  onStateSelect={handleStateSelect}
                  stateWiseData={stateWiseData}
                  stateWiseSchemeData={stateWiseSchemeData}
                    showAllStates={currentView === 'district' ? true : (!selectedState && !!selectedScheme)}
                />

                <MaskLayer
                  selectedState={selectedState}
                  selectedDistrict={selectedDistrict}
                  stateData={stateData}
                  selectedVillage={selectedVillage}
                />

                {!selectedState && stateData && (
                  <GeoJSON
                    key="all-states"
                    data={stateData}
                    onEachFeature={onEachFeature}
                    style={{ color: "white", weight: 1, fillOpacity: 0.0, zIndex: 100 }}
                  />
                )}
                
                {selectedState && (
                  <>
                    <GeoJSON
                      key={`state-${selectedState.properties.STATE}`}
                      data={selectedState}
                      onEachFeature={onEachFeature}
                      style={{ color: "transparent", weight: 2, fillOpacity: 0.3, zIndex: 100 }}
                    />
                    {filteredDistricts.length > 0 && (
                      <GeoJSON
                        key={`districts-${selectedState.properties.STATE}`}
                        data={{ type: "FeatureCollection", features: filteredDistricts }}
                        onEachFeature={onEachFeature}
                        style={{ color: "transparent", weight: 2, fillOpacity: 0.2, zIndex: 200 }}
                      />
                    )}
                  </>
                )}

                {selectedDistrict && (
                  <GeoJSON
                    key={`district-${selectedDistrict.properties.DISTRICT}`}
                    data={selectedDistrict}
                    onEachFeature={onEachFeature}
                    style={{ color: "transparent", weight: 2, fillOpacity: 0.4, zIndex: 300 }}
                  />
                )}
                
                {/* UPDATED: DistrictSchemeVisualization integration */}
                {selectedState && (
   <DistrictSchemeVisualization
    selectedState={selectedState}
    stateData={stateData}
    districtData={districtData} // Make sure this is loaded
    selectedScheme={selectedScheme}
    selectedFinancialYear={selectedFinancialYear}
    selectedDistrict={selectedDistrict}
    onDistrictClick={handleDistrictClick}
    districtWiseData={districtWiseData}
    districtWiseSchemeData={districtWiseSchemeData}
    loadingDistrictData={loadingDistrictData}
  />
                )}

                {(selectedScheme && selectedDistrictForPolygons) && (
                  <PolygonLayer
                    selectedScheme={selectedScheme}
                    selectedDistrict={selectedDistrictForPolygons}
                    selectedFinancialYear={selectedFinancialYear}
                    schemeColors={schemeColors}
                    onPolygonsLoaded={(polygons) => {
                      console.log(`🎉 Loaded ${polygons.length} polygons for scheme`);
                    }}
                    setShowATMAPopup={setShowATMAPopup}
  setATMAPopupData={setSelectedATMAPopupData}
  setShowBeneficiaryModal={setShowBeneficiaryModal}
  setSelectedBeneficiaryData={setSelectedBeneficiaryData}
  setIsSidebarMinimized={setIsSidebarMinimized} 
                  />
                )}

                {filteredVillages.length > 0 && (
                  <GeoJSON
                    key={`villages-${selectedDistrict?.properties.DISTRICT || 'none'}`}
                    data={{ type: "FeatureCollection", features: filteredVillages }}
                    onEachFeature={onEachFeature}
                    style={{ color: "transparent", weight: 1, fillOpacity: 0.5, zIndex: 400 }}
                  />
                )}
              </MapContainer>
            </div>
          </div>

         {/* Sidebar Column - Only show when not minimized */}
{!isSidebarMinimized && (
  <div className="col-md-4 px-0">
    <div className="boxRightBox">
      <div className="mapRightbox">
        {displayAnalytics && (
          <div className="show-analytics">
            <div className="col-md-12 mb-2">
              
              {/* PRIORITY 1: Show ATMA Sidebar Details when ATMA popup is active */}
              {showATMAPopup && selectedATMAPopupData ? (
                <ATMASidebarDetails 
                  data={selectedATMAPopupData}
                  onClose={() => {
                    setShowATMAPopup(false);
                    setSelectedATMAPopupData(null);
                     setIsSidebarMinimized(true);
                  }}
                  onViewBeneficiaryDetails={(beneficiary, index) => {
                    setSelectedBeneficiaryData(beneficiary);
                    setShowBeneficiaryModal(true);
                  }}
                />
              ) : (
                <>
                  {/* PRIORITY 2: Show District Table when state is selected AND no district is clicked */}
                  {selectedState && showDistrictTable && !selectedDistrict ? (
                    <DistrictSchemeTable
                      selectedState={selectedState}
                      selectedScheme={selectedScheme}
                      selectedFinancialYear={selectedFinancialYear}
                      districtWiseData={districtWiseData}
                      loadingDistrictData={loadingDistrictData}
                    />
                  ) : (
                    <>
                      {/* Show Top Schemes when no scheme is selected */}
                      {!selectedScheme && showTopSchemes && (
                        <TopSchemesCard
                          topSchemes={topSchemes}
                          isLoading={isTopSchemesLoading}
                          selectedFinancialYear={selectedFinancialYear}
                        />
                      )}

                      {/* Show State-wise table when scheme is selected, OR All Schemes table when no scheme */}
                      {selectedScheme ? (
                        <div className="state-wise-data-container">
                          {loadingStateData ? (
                            <div className="text-center py-4">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                            </div>
                          ) : stateWiseData.length > 0 ? (
                            <div className="mapRightBhuvanAnalytics mt-2">
                              <div className="mapheader">
                                <h2 className="mb-0">
                                  State-wise Counts - {selectedScheme.schemeName}
                                  {selectedFinancialYear && ` (FY: ${selectedFinancialYear})`}
                                </h2>
                              </div>
                              <div className="StatewiseScroll">
                                <div className="table-responsive">
                                  <table className="table table-bordered table-striped table-hover">
                                    <thead>
                                      <tr>
                                        <th>State</th>
                                        <th>Count</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {stateWiseData.map((item, index) => {
                                        if (!item) return null;
                                        const stateName = item.stateName || item.State || item.name || 'N/A';
                                        const count = item.totalSurvey || item.count || item.surveyCount || 0;
                                        return (
                                          <tr key={`${stateName}-${index}`}>
                                            <td>{stateName}</td>
                                            <td>{parseInt(count).toLocaleString()}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                    <tfoot>
                                      <tr className="fw-bold">
                                        <td>Total</td>
                                        <td>
                                          {stateWiseData.reduce(
                                            (sum, item) => sum + (parseInt(item.totalSurvey || item.count || 0)),
                                            0
                                          ).toLocaleString()}
                                        </td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="card mb-3">
                              <div className="card-body text-center">
                                <p className="text-muted mb-0">
                                  No data available for {selectedScheme.schemeName}
                                  {selectedFinancialYear && ` in FY: ${selectedFinancialYear}`}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <SchemeCountsTable
                          schemeCounts={schemeCounts}
                          schemeColors={schemeColors}
                          financialYear={selectedFinancialYear}
                          isLoading={isFinancialYearLoading}
                        />
                      )}
                    </>
                  )}
                </>
              )}
              
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
)}

{/* Beneficiary Details Modal - This stays outside the sidebar */}
{showBeneficiaryModal && selectedBeneficiaryData && (
  <BeneficiaryDetailsModal 
    beneficiary={selectedBeneficiaryData}
    onClose={() => {
      setShowBeneficiaryModal(false);
      setSelectedBeneficiaryData(null);
    }}
  />
)}


        </div>
      </div>

      {/* CSS for the toggle button */}
      <style jsx>{`
        .minimize-toggle-btn {
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 24px;
          height: 60px;
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-right: none;
          border-radius: 4px 0 0 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 1000;
          transition: all 0.3s ease;
          font-size: 12px;
          color: #6c757d;
        }
        
        .minimize-toggle-btn:hover {
          background: #e9ecef;
          color: #495057;
        }
        
        .position-relative {
          position: relative;
        }
      `}</style>
    </div>
  );
};

export default GIS;